/**
 * The command palette's "Pages" group — the app's own navigable destinations.
 *
 * WHY A REGISTRY AND NOT THE ROUTER
 * `router.tsx` knows every path but nothing a palette needs: no human label, no
 * synonyms, and no distinction between a destination a user jumps to
 * ("Settings → Billing") and a path that only exists to receive an id
 * (`jobs/:jdId/edit`). Deriving from the route table would surface parameterised
 * junk and force a second table of labels anyway. So: an explicit registry, with
 * the router as the authority on WHERE each entry points and on which entries
 * are admin-gated.
 *
 * RBAC — this list is filtered, never shown whole.
 * Each entry declares the gate its own nav surface already applies, and
 * `useNavDestinations()` evaluates them:
 *
 *   - `capability` → `useCapability(...)`, the server-computed capset from
 *     `/auth/me`. Fails closed while it loads, so a gated row is hidden-until-
 *     known rather than flashing in.
 *   - `adminOnly`  → `useIsTenantAdmin()`, the role-shaped gate for the pages
 *     with no matching capability.
 *
 * The gates are COPIED FROM the surfaces that already own them, so the palette
 * cannot become a back door into a page its own nav hides:
 *   - sidebar rows          → `SidebarV2` (Workspace / Insights / Support are
 *                             all-roles; ACCOUNT is admin-gated)
 *   - settings sub-pages    → `SettingsHubV2` card-by-card gates, verbatim
 *   - Roles                 → `RequireAdmin` on the route (no hub card)
 *   - Profile               → `AccountMenuV2` (all roles)
 *
 * This is UX hygiene, NOT authorization: the backend is the hard gate, and
 * `RequireAdmin` still redirects anyone who reaches an admin route another way.
 * The value here is that we never offer a jump that dead-ends in a redirect.
 *
 * DELIBERATELY ABSENT: "New Job" / "Add Candidate" style ACTIONS. The palette
 * ships as a jump-to affordance; action rows are a separate design question
 * (they mutate, they need their own capability gates, and they change what
 * Enter means on a highlighted row).
 */
import { useMemo } from "react";
import { useCapability } from "@/hooks/useCapability";
import { useIsTenantAdmin } from "@/hooks/useIsTenantAdmin";
import { useTenantBase } from "@/hooks/useTenantBase";
import type { Capability } from "@/lib/capabilities";

interface NavDestination {
  /** Stable id — the palette's React key and its `aria-activedescendant` target. */
  id: string;
  /** Row title. Matches the label the corresponding nav surface uses. */
  label: string;
  /** Muted breadcrumb-ish subtitle, e.g. "Settings". Omitted for top-level pages. */
  section?: string;
  /** Path RELATIVE to the tenant base; `useNavDestinations` prefixes it. */
  path: string;
  /**
   * Extra match terms that never render. These are the words people actually
   * type — the old name of a page, the thing they think it is called, the
   * noun on the page rather than in the nav. Matching label-only would make
   * the palette feel broken for anyone who does not already know the IA.
   */
  keywords?: string[];
  /** Capability required, mirroring the owning nav surface. */
  capability?: Capability;
  /** Admin-or-above required, for pages no capability covers. */
  adminOnly?: true;
}

/**
 * The catalog, in the order the palette renders matches.
 *
 * Order is nav order (sidebar top-to-bottom, then Settings), not alphabetical:
 * with a short query the top hit should be the destination the user reaches for
 * most, and nav order is the closest proxy we have.
 */
const DESTINATIONS: readonly NavDestination[] = [
  // ── Sidebar: WORKSPACE (all roles) ──────────────────────────────────────
  {
    id: "nav-dashboard",
    label: "Dashboard",
    path: "/dashboard",
    keywords: ["home", "overview", "start"],
  },
  {
    id: "nav-candidates",
    label: "All Candidates",
    path: "/candidates",
    keywords: ["people", "applicants", "resumes", "résumés", "pipeline"],
  },
  {
    id: "nav-jobs",
    label: "Manage Jobs",
    path: "/jobs",
    keywords: ["jds", "job descriptions", "reqs", "requisitions", "roles", "openings"],
  },

  // ── Sidebar: INSIGHTS (all roles) ───────────────────────────────────────
  {
    id: "nav-intelligence",
    label: "Hiring Intelligence",
    path: "/intelligence",
    keywords: ["analytics", "insights", "reporting", "metrics", "dashboard"],
  },
  {
    id: "nav-outcomes",
    label: "Outcomes",
    path: "/outcomes",
    keywords: ["placements", "hires", "post-placement", "results", "tracking"],
  },
  {
    id: "nav-journal",
    label: "Actions Log",
    path: "/journal",
    keywords: ["journal", "history", "audit", "timeline", "activity"],
  },

  // ── Account menu (all roles) ────────────────────────────────────────────
  {
    id: "nav-profile",
    label: "Profile",
    path: "/profile",
    keywords: ["account", "me", "my details", "name", "password"],
  },

  // ── Sidebar: ACCOUNT — admin-gated group ────────────────────────────────
  {
    id: "nav-settings",
    label: "Settings",
    path: "/settings",
    keywords: ["preferences", "configuration", "workspace", "admin"],
    adminOnly: true,
  },

  // ── Settings sub-pages — gates copied card-for-card from SettingsHubV2 ──
  {
    id: "nav-settings-team",
    label: "Team",
    section: "Settings",
    path: "/settings/team",
    keywords: ["users", "members", "invite", "seats", "colleagues"],
    capability: "team_view",
  },
  {
    id: "nav-settings-ai",
    label: "AI Settings",
    section: "Settings",
    path: "/settings/ai",
    keywords: ["model", "scoring", "weights", "prompts", "calibration", "tuning"],
  },
  {
    id: "nav-settings-integrations",
    label: "Integrations",
    section: "Settings",
    path: "/settings/integrations",
    keywords: ["greenhouse", "ats", "teams", "connect", "sync", "api key"],
  },
  {
    id: "nav-settings-branding",
    label: "Branding",
    section: "Settings",
    path: "/settings/branding",
    keywords: ["white label", "logo", "colors", "colours", "exports", "pdf"],
    capability: "settings_manage",
  },
  {
    id: "nav-settings-billing",
    label: "Billing",
    section: "Settings",
    path: "/settings/billing",
    keywords: ["plan", "invoice", "payment", "card", "subscription", "credits"],
    capability: "billing_view",
  },
  {
    id: "nav-settings-usage",
    label: "Usage",
    section: "Settings",
    path: "/settings/usage",
    keywords: ["quota", "screens", "consumption", "limits", "credits"],
    adminOnly: true,
  },
  {
    id: "nav-settings-security",
    label: "Security",
    section: "Settings",
    path: "/settings/security",
    keywords: ["mfa", "2fa", "passkey", "webauthn", "sessions", "sso"],
    capability: "settings_manage",
  },
  {
    id: "nav-settings-compliance",
    label: "Compliance",
    section: "Settings",
    path: "/settings/compliance",
    keywords: ["eeoc", "gdpr", "retention", "erasure", "bias audit", "legal"],
    capability: "settings_manage",
  },
  {
    id: "nav-settings-ai-oversight",
    label: "AI Oversight",
    section: "Settings",
    path: "/settings/compliance/ai-oversight",
    keywords: ["human in the loop", "review", "decisions", "explainability"],
  },
  {
    id: "nav-settings-activity",
    label: "Activity log",
    section: "Settings",
    path: "/settings/activity",
    keywords: ["audit trail", "events", "who did what"],
    adminOnly: true,
  },
  {
    id: "nav-settings-sessions",
    label: "Sessions",
    section: "Settings",
    path: "/settings/sessions",
    keywords: ["devices", "sign out", "logins", "active sessions"],
  },
  {
    id: "nav-settings-roles",
    label: "Roles",
    section: "Settings",
    path: "/settings/roles",
    keywords: ["permissions", "capabilities", "access", "rbac"],
    // No hub card; the ROUTE is `RequireAdmin`, so mirror that.
    adminOnly: true,
  },

  // ── Sidebar: SUPPORT (all roles) ────────────────────────────────────────
  {
    id: "nav-help",
    label: "Help Center",
    path: "/help",
    keywords: ["docs", "documentation", "support", "guide", "how to", "faq"],
  },
] as const;

/**
 * A destination with its absolute in-app href resolved.
 *
 * `href` is separate from `path` because the registry is tenant-agnostic (it is
 * module-level constant data) while the link needs the tenant base from the
 * router.
 */
export interface ResolvedNavDestination extends NavDestination {
  href: string;
}

/**
 * The destinations THIS user may see, tenant-resolved.
 *
 * Every gate is evaluated unconditionally at the top (hooks cannot be called in
 * a loop or a branch), then applied while filtering. That is why the capability
 * list here is a flat set of `useCapability` calls rather than something driven
 * off the registry — the Rules of Hooks make the data-driven version illegal.
 * A `capability` value added to the registry without a matching call below
 * would silently never gate; the unit test asserts every declared capability
 * appears in the map.
 */
export function useNavDestinations(): ResolvedNavDestination[] {
  const base = useTenantBase();
  const isAdmin = useIsTenantAdmin();

  const canViewTeam = useCapability("team_view");
  const canViewBilling = useCapability("billing_view");
  const canManageSettings = useCapability("settings_manage");

  return useMemo(() => {
    const granted: Record<Capability, boolean> = {
      team_view: canViewTeam,
      billing_view: canViewBilling,
      settings_manage: canManageSettings,
      // Not used as a palette gate today — no destination declares them.
      // Present so the record is total over `Capability` and a future entry
      // cannot compile against a missing key.
      jd_create: false,
      jd_view_all: false,
      candidate_create: false,
      integrations_manage: false,
      users_manage: false,
    };

    return DESTINATIONS.filter((d) => {
      if (d.adminOnly === true && !isAdmin) return false;
      if (d.capability !== undefined && !granted[d.capability]) return false;
      return true;
    }).map((d) => ({ ...d, href: `${base}${d.path}` }));
  }, [base, isAdmin, canViewTeam, canViewBilling, canManageSettings]);
}

/**
 * Substring match over label, section, and keywords — case- and accent-loose.
 *
 * Not fuzzy. A palette over ~20 destinations does not need edit distance, and
 * fuzzy matching's failure mode (confident nonsense at the top of the list) is
 * worse here than a miss: the list is short enough to scan, and the server
 * groups below it are what the user usually wants.
 *
 * Exported for the unit test; the palette calls it through `useNavDestinations`
 * results.
 */
export function matchNavDestinations(
  destinations: readonly ResolvedNavDestination[],
  query: string,
): ResolvedNavDestination[] {
  const needle = normalizeForMatch(query);
  if (needle.length === 0) return [];
  return destinations.filter((d) => {
    if (normalizeForMatch(d.label).includes(needle)) return true;
    if (d.section !== undefined && normalizeForMatch(d.section).includes(needle)) return true;
    return (d.keywords ?? []).some((k) => normalizeForMatch(k).includes(needle));
  });
}

/**
 * Lower-case and strip diacritics so "resume" finds "résumés".
 *
 * NFD splits an accented character into base + combining mark; the range strip
 * then drops the marks. Recruiters type ASCII; the corpus and the nav do not
 * always contain it.
 */
function normalizeForMatch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}
